import React, { Suspense, lazy } from 'react';
import { render } from 'react-dom';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AnimatedPageLoader } from 'smartsuite-ui';

import ErrorBoundary from './components/ErrorBoundary';
import DefaultRoute from './components/DefaultRoute';
import { toastContainerProps } from './constants/toastConstants';

import 'smartsuite-ui/dist/smartsuite-ui.css';
import 'react-toastify/dist/ReactToastify.css';
import './styles/react-toastify-overrides.css';

import './i18n';

const AdvancedSearchAppWrapper = lazy(() =>
    import('./apps/AdvancedSearch/AdvancedSearchAppWrapper')
);
const ConfigurationAppWrapper = lazy(() =>
    import('./apps/Configuration/ConfigurationAppWrapper')
);

const ActiveBerthingScheduleApp = lazy(() =>
    import('./apps/ActiveBerthingSchedule/App')
);
const BerthingActivityReviewApp = lazy(() =>
    import('./apps/BerthingActivityReview/App')
);
const BerthingDetailsApp = lazy(() => import('./apps/BerthingDetails/App'));
const BerthingScheduleTemplateApp = lazy(() =>
    import('./apps/BerthingScheduleTemplate/App')
);
const BoardingPassPrintPreviewApp = lazy(() =>
    import('./apps/BoardingPassPrintPreview/App')
);
const CameraStatisticsApp = lazy(() => import('./apps/CameraStatistics/App'));
const DamageReportApp = lazy(() => import('./apps/DamageReport/App'));
const DamageReportsApp = lazy(() => import('./apps/DamageReports/App'));
const ExitGateApp = lazy(() => import('./apps/ExitGate/App'));
const FreightSecurityHutApp = lazy(() =>
    import('./apps/FreightSecurityHut/App')
);
const HazardReportsApp = lazy(() => import('./apps/HazardReports/App'));
const InYardDwellTimeApp = lazy(() => import('./apps/InYardDwellTime/App'));
const RemoteYardApp = lazy(() => import('./apps/RemoteYard/App'));
const RemoteWeighBridgeApp = lazy(() => import('./apps/RemoteWeighBridge/App'));
const OrphanedManifestsApp = lazy(() => import('./apps/OrphanedManifests/App'));
const OrphanedVehiclesApp = lazy(() => import('./apps/OrphanedVehicles/App'));
const OrphanedVoyagesApp = lazy(() => import('./apps/OrphanedVoyages/App'));
const PassengerCheckInHutApp = lazy(() =>
    import('./apps/PassengerCheckInHut/App')
);
const PickUpDropOffHutApp = lazy(() => import('./apps/PickUpDropOffHut/App'));
const QuaysideOverviewApp = lazy(() => import('./apps/QuaysideOverview/App'));
const TrafficCoordinatorApp = lazy(() =>
    import('./apps/TrafficCoordinator/App')
);
const TugManagementApp = lazy(() => import('./apps/TugManagement/App'));
const TugOperatorDashboardApp = lazy(() =>
    import('./apps/TugOperatorDashboard/App')
);
const VehicleDetailsApp = lazy(() => import('./apps/VehicleDetails/App'));
const VehicleSightingSearchApp = lazy(() =>
    import('./apps/VehicleSightingSearch/App')
);
const WeighBridgeApp = lazy(() => import('./apps/WeighBridge/App'));
const YardAuditApp = lazy(() => import('./apps/YardAudit/App'));
const YardOverviewApp = lazy(() => import('./apps/YardOverview/App'));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount, error) =>
                error.statusCode !== 403 && failureCount < 2,
        },
    },
});

const rootElement = document.getElementById('root');

render(
    <ErrorBoundary>
        <Suspense fallback={<AnimatedPageLoader />}>
            <Router basename={window.rootUrl}>
                <Switch>
                    <QueryClientProvider client={queryClient}>
                        <Route
                            path="/apps/AdvancedSearch"
                            component={AdvancedSearchAppWrapper}
                        />
                        <Route
                            path="/apps/Configuration"
                            component={ConfigurationAppWrapper}
                        />
                        <Route
                            path="/apps/activeBerthingSchedule/:mode?"
                            component={ActiveBerthingScheduleApp}
                        />
                        <Route
                            path="/apps/berthingActivityReview/:id?/:direction?/:tab?"
                            component={BerthingActivityReviewApp}
                        />
                        <Route
                            path="/apps/berthingDetails/:id?"
                            component={BerthingDetailsApp}
                        />
                        <Route
                            path="/apps/berthingScheduleTemplate"
                            component={BerthingScheduleTemplateApp}
                        />
                        <Route
                            path="/apps/boardingPassPrintPreview/:tempAccompaniedCheckInDataId"
                            component={BoardingPassPrintPreviewApp}
                        />
                        <Route
                            path="/apps/cameraStatistics"
                            component={CameraStatisticsApp}
                        />
                        <Route
                            path="/apps/damageReport/:mode?/:id?"
                            component={DamageReportApp}
                        />
                        <Route
                            path="/apps/damageReports"
                            component={DamageReportsApp}
                        />
                        <Route path="/apps/exitGate" component={ExitGateApp} />
                        <Route
                            path="/apps/freightSecurityHut"
                            component={FreightSecurityHutApp}
                        />
                        <Route
                            path="/apps/hazardReports"
                            component={HazardReportsApp}
                        />
                        <Route
                            path="/apps/inYardDwellTime"
                            component={InYardDwellTimeApp}
                        />
                        <Route
                            path="/apps/remoteYard"
                            component={RemoteYardApp}
                        />
                        <Route
                            path="/apps/remoteWeighBridge"
                            component={RemoteWeighBridgeApp}
                        />
                        <Route
                            path="/apps/orphanedManifests"
                            component={OrphanedManifestsApp}
                        />
                        <Route
                            path="/apps/orphanedVehicles"
                            component={OrphanedVehiclesApp}
                        />
                        <Route
                            path="/apps/orphanedVoyages"
                            component={OrphanedVoyagesApp}
                        />
                        <Route
                            path="/apps/quaysideOverview/:mode?"
                            component={QuaysideOverviewApp}
                        />
                        <Route
                            path="/apps/passengerCheckInHut"
                            component={PassengerCheckInHutApp}
                        />
                        <Route
                            path="/apps/pickUpDropOffHut"
                            component={PickUpDropOffHutApp}
                        />
                        <Route
                            path="/apps/trafficCoordinator"
                            component={TrafficCoordinatorApp}
                        />
                        <Route
                            path="/apps/tugManagement"
                            component={TugManagementApp}
                        />
                        <Route
                            path="/apps/tugOperatorDashboard"
                            component={TugOperatorDashboardApp}
                        />
                        <Route
                            path="/apps/vehicleDetails/:id?"
                            component={VehicleDetailsApp}
                        />
                        <Route
                            path="/apps/vehicleSightingSearch"
                            component={VehicleSightingSearchApp}
                        />
                        <Route
                            path="/apps/weighBridge"
                            component={WeighBridgeApp}
                        />
                        <Route
                            path="/apps/yardAudit"
                            component={YardAuditApp}
                        />
                        <Route
                            path="/apps/yardOverview"
                            component={YardOverviewApp}
                        />
                        <Route exact={true} path="/" component={DefaultRoute} />
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </Switch>
            </Router>
            <ToastContainer {...toastContainerProps} />
        </Suspense>
    </ErrorBoundary>,
    rootElement
);
